import { useCallback, useEffect, useState, type InputHTMLAttributes } from "react";
import { useGT } from "gt-react";
import {
  DEFAULT_AUTH_EDITOR_FIELDS,
  getCanonicalAuthType,
  parseStoredAuthToEditorFields,
  serializeAuthConfig,
} from "@spuerhund/shared/auth";
import { defaultServerConnectionConfig, normalizeServerKind, type ServerKind } from "@spuerhund/shared/targets";
import type { StoredServer } from "../persistence";
import {
  addServer,
  createOrUpdateSharedConfig,
  deleteCurrentSharedConfig,
  deleteServer,
  introspectSchema,
  updateServer,
} from "../store";
import {
  inviteToSharedConfig,
  listSharedConfigMembers,
  listSharedConfigPendingInvites,
  kickSharedConfigMember,
  changeSharedConfigMemberRole,
  resendSharedConfigInvite,
  revokeSharedConfigInvite,
  transferSharedConfigOwnership,
  type SharedConfigMember,
  type SharedConfigPendingInvite,
} from "../sharedConfigClient";
import { useAuthStore } from "../authStore";
import { toast } from "./Toasts";
import {
  Check,
  Copy,
  Crown,
  Mail,
  RefreshCw,
  ShieldAlert,
  Trash2,
  User,
  UserPlus,
  WandSparkles,
  X,
} from "lucide-react";
import { VaultReferencePicker } from "./VaultReferencePicker";

type AuthType = "none" | "bearer" | "apikey" | "custom";

interface ServerDialogProps {
  editingServer?: StoredServer;
  onClose: () => void;
}

function normalizeOptionalPath(value: string): string | undefined {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  return trimmed;
}

function appendVaultReference(currentValue: string, template: string): string {
  return `${currentValue}${template}`;
}

function VaultInsertField({
  value,
  onChange,
  serverId,
  preferredKind = "secret",
  inputClassName,
  wrapperClassName,
  ...props
}: Omit<InputHTMLAttributes<HTMLInputElement>, "value" | "onChange"> & {
  value: string;
  onChange: (value: string) => void;
  serverId?: string | undefined;
  preferredKind?: "secret" | "config";
  inputClassName: string;
  wrapperClassName?: string;
}) {
  return (
    <div className={wrapperClassName ?? "flex items-center gap-2"}>
      <input
        {...props}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className={inputClassName}
      />
      <VaultReferencePicker
        serverId={serverId}
        preferredKind={preferredKind}
        onInsert={(template) => onChange(appendVaultReference(value, template))}
      />
    </div>
  );
}

function buildHeaderScriptAgentPrompt(input: {
  serverName: string;
  baseUrl: string;
  graphqlPath: string;
  restPath: string;
}): string {
  const baseUrl = input.baseUrl.trim() || "(not provided yet)";
  const graphqlPath = input.graphqlPath.trim() || "(use base URL directly)";
  const restPath = input.restPath.trim() || "(use base URL directly)";

  return `You are a software developer helping a user create a Spürhund custom header script for authenticating requests.

First, guide the user with focused questions instead of dumping assumptions.

Known context:
- Server name: ${input.serverName.trim() || "(not provided yet)"}
- Base URL: ${baseUrl}
- GraphQL path: ${graphqlPath}
- REST path: ${restPath}

Your job:
1. Ask what the user already knows about the target service authentication.
2. If the user does not know, ask for the target service URL and inspect public docs, auth hints, or obvious protocol clues.
3. Ask only the missing questions needed to produce a working script.
4. Finish with exactly one copy-paste-ready Spürhund script.

Spürhund runtime:
- The code is pasted into "Custom Headers -> Header Script".
- It runs as an async function body.
- Available helpers:
  - await secret("key")
  - await config("key")
  - fetch(...)

Preferred return contract:
\`\`\`js
return {
  headers: {
    Authorization: \`Bearer \${token}\`,
    "X-Custom": value,
  },
  expiresAt: new Date(expiryTimestamp).toISOString(),
};
\`\`\`

Notes:
- The script may also return only a plain header object, but prefer \`expiresAt\` whenever token expiry can be determined.
- Do not hardcode secrets if a secret() or config() lookup is more appropriate.
- If the service requires multiple headers, return all of them.
- If browser automation or manual login would be required, explain the limitation clearly and then propose the smallest workable script.

Output only the final script in a code block once the user has answered enough questions.`;
}

function parseHeaderRows(json: string): Array<{ key: string; value: string }> {
  try {
    const obj = JSON.parse(json);
    const headers = typeof obj === "object" && obj !== null && "headers" in obj && typeof obj.headers === "object" && obj.headers !== null
      ? obj.headers
      : obj;
    if (typeof headers === "object" && headers !== null) {
      const rows = Object.entries(headers).map(([key, value]) => ({ key, value: String(value) }));
      if (rows.length > 0) return [...rows, { key: "", value: "" }];
    }
  } catch {
    // ignore parse errors in the inline grid editor
  }
  return [{ key: "", value: "" }];
}

function rowsToJson(rows: Array<{ key: string; value: string }>): string {
  const obj: Record<string, string> = {};
  for (const row of rows) {
    const key = row.key.trim();
    if (key) obj[key] = row.value;
  }
  return JSON.stringify({ headers: obj });
}

function CustomHeadersEditor({
  value,
  onChange,
  serverId,
}: {
  value: string;
  onChange: (v: string) => void;
  serverId?: string | undefined;
}) {
  const t = useGT();
  const [rows, setRows] = useState(() => parseHeaderRows(value));

  useEffect(() => {
    setRows(parseHeaderRows(value));
  }, [value]);

  const updateRow = (index: number, field: "key" | "value", text: string) => {
    const next = rows.map((row, rowIndex) => (rowIndex === index ? { ...row, [field]: text } : row));
    const last = next[next.length - 1];
    if (last && (last.key || last.value)) {
      next.push({ key: "", value: "" });
    }
    setRows(next);
    onChange(rowsToJson(next));
  };

  const removeRow = (index: number) => {
    const next = rows.filter((_, rowIndex) => rowIndex !== index);
    if (next.length === 0) next.push({ key: "", value: "" });
    setRows(next);
    onChange(rowsToJson(next));
  };

  return (
    <div>
      <label className="mb-1 block text-[11px] font-medium text-zinc-500">{t("Custom Headers")}</label>
      <div className="space-y-1">
        {rows.map((row, index) => {
          const isLast = index === rows.length - 1 && !row.key && !row.value;
          return (
            <div key={index} className="flex items-center gap-1">
              <input
                type="text"
                value={row.key}
                onChange={(event) => updateRow(index, "key", event.target.value)}
                placeholder={t("Header-Name")}
                className="w-[40%] rounded bg-surface px-2 py-1 font-mono text-xs text-zinc-200 outline-none ring-1 ring-border placeholder:text-zinc-700 focus:ring-accent"
              />
              <VaultInsertField
                type="text"
                value={row.value}
                onChange={(nextValue) => updateRow(index, "value", nextValue)}
                placeholder={t("Value")}
                serverId={serverId}
                preferredKind="secret"
                wrapperClassName="flex min-w-0 flex-1 items-center gap-2"
                inputClassName="flex-1 rounded bg-surface px-2 py-1 font-mono text-xs text-zinc-200 outline-none ring-1 ring-border placeholder:text-zinc-700 focus:ring-accent"
              />
              {!isLast ? (
                <button
                  type="button"
                  onClick={() => removeRow(index)}
                  className="shrink-0 rounded p-0.5 text-zinc-600 hover:text-danger"
                  title={t("Remove header")}
                >
                  <X size={12} />
                </button>
              ) : (
                <div className="w-[20px] shrink-0" />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function formatRelativeTime(iso?: string | null): string {
  if (!iso) return "never";
  const diffMs = Date.now() - new Date(iso).getTime();
  if (diffMs < 60_000) return "just now";
  const minutes = Math.floor(diffMs / 60_000);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function formatTimeUntil(iso?: string | null): string {
  if (!iso) return "unknown";
  const diffMs = new Date(iso).getTime() - Date.now();
  if (diffMs <= 0) return "expired";
  if (diffMs < 60_000) return "in <1m";
  const minutes = Math.floor(diffMs / 60_000);
  if (minutes < 60) return `in ${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `in ${hours}h`;
  const days = Math.floor(hours / 24);
  return `in ${days}d`;
}

export function ServerDialog({ editingServer, onClose }: ServerDialogProps) {
  const t = useGT();
  const isEditing = !!editingServer;
  const currentServerId = editingServer?.id;

  const initialAuth = editingServer
    ? parseStoredAuthToEditorFields(editingServer.authType, editingServer.authConfig)
    : DEFAULT_AUTH_EDITOR_FIELDS;
  const initialConnection = editingServer ? defaultServerConnectionConfig(editingServer) : {
    kind: "http" as const,
    baseUrl: "",
    graphqlPath: "",
    restPath: "",
  };

  const [name, setName] = useState(editingServer?.name ?? "");
  const [serverKind, setServerKind] = useState<ServerKind>(normalizeServerKind(editingServer?.serverKind));
  const [baseUrl, setBaseUrl] = useState(editingServer?.baseUrl ?? "");
  const [graphqlPath, setGraphqlPath] = useState(editingServer?.graphqlPath ?? "");
  const [restPath, setRestPath] = useState(editingServer?.restPath ?? "");
  const [connectionUri, setConnectionUri] = useState(
    initialConnection.kind === "mongo" || initialConnection.kind === "postgres" ? initialConnection.connectionUri : "",
  );
  const [database, setDatabase] = useState(initialConnection.kind === "mongo" ? initialConnection.database : "");
  const [defaultSchema, setDefaultSchema] = useState(initialConnection.kind === "postgres" ? initialConnection.defaultSchema ?? "" : "");
  const [authType, setAuthType] = useState<AuthType>(
    editingServer ? getCanonicalAuthType(editingServer.authType, editingServer.authConfig) : "none",
  );
  const [token, setToken] = useState(initialAuth.token);
  const [headerName, setHeaderName] = useState(initialAuth.headerName);
  const [apiKey, setApiKey] = useState(initialAuth.apiKey);
  const [customMode, setCustomMode] = useState(initialAuth.customMode);
  const [customHeaders, setCustomHeaders] = useState(initialAuth.customHeaders);
  const [customScript, setCustomScript] = useState(initialAuth.customScript);
  const [isProduction, setIsProduction] = useState(editingServer?.isProduction ?? false);
  const [noTimeout, setNoTimeout] = useState(editingServer?.timeoutMs === 0);
  const [timeoutSeconds, setTimeoutSeconds] = useState(editingServer?.timeoutMs != null && editingServer.timeoutMs > 0 ? String(editingServer.timeoutMs / 1000) : "");
  const [saving, setSaving] = useState(false);
  const [scriptPromptCopied, setScriptPromptCopied] = useState(false);
  const [copiedInviteId, setCopiedInviteId] = useState<string | null>(null);
  const [sharingBusy, setSharingBusy] = useState(false);
  const [members, setMembers] = useState<SharedConfigMember[]>([]);
  const [pendingInvites, setPendingInvites] = useState<SharedConfigPendingInvite[]>([]);
  const [inviteEmail, setInviteEmail] = useState("");
  const [transferTarget, setTransferTarget] = useState<SharedConfigMember | null>(null);

  const currentUser = useAuthStore((s) => s.user);
  const isShared = !!editingServer?.sharedConfigId;
  const sharedConfigId = editingServer?.sharedConfigId;
  const currentUserRole = members.find((m) => m.userId === currentUser?.id)?.role;
  const isOwner = currentUserRole === "owner";

  const isHttp = serverKind === "http";
  const canSave = name.trim() !== ""
    && (isHttp
      ? baseUrl.trim() !== ""
      : connectionUri.trim() !== "" && (serverKind === "mongo" ? database.trim() !== "" : true));

  const loadMembers = useCallback(async () => {
    if (!sharedConfigId) {
      setMembers([]);
      return;
    }
    try {
      const { members: fetched } = await listSharedConfigMembers(sharedConfigId);
      setMembers(fetched);
    } catch {
      setMembers([]);
    }
  }, [sharedConfigId]);

  const loadPendingInvites = useCallback(async () => {
    if (!sharedConfigId || !isOwner) {
      setPendingInvites([]);
      return;
    }
    try {
      const { invites } = await listSharedConfigPendingInvites(sharedConfigId);
      setPendingInvites(invites);
    } catch {
      setPendingInvites([]);
    }
  }, [isOwner, sharedConfigId]);

  useEffect(() => {
    void loadMembers();
  }, [loadMembers]);

  useEffect(() => {
    void loadPendingInvites();
  }, [loadPendingInvites]);

  const handleSave = async () => {
    if (!canSave || saving) return;
    setSaving(true);

    const authConfig = serializeAuthConfig(authType, {
      token,
      headerName,
      apiKey,
      customMode,
      customHeaders,
      customScript,
    });

    try {
      const paths = {
        graphqlPath: normalizeOptionalPath(graphqlPath),
        restPath: normalizeOptionalPath(restPath),
      };
      const connectionConfig = serverKind === "http"
        ? {
            kind: "http" as const,
            baseUrl,
            ...(paths.graphqlPath !== undefined ? { graphqlPath: paths.graphqlPath } : {}),
            ...(paths.restPath !== undefined ? { restPath: paths.restPath } : {}),
          }
        : serverKind === "mongo"
          ? {
              kind: "mongo" as const,
              connectionUri,
              database,
            }
          : {
              kind: "postgres" as const,
              connectionUri,
              ...(defaultSchema.trim() ? { defaultSchema: defaultSchema.trim() } : {}),
            };

      const validTimeoutMs = noTimeout
        ? 0
        : timeoutSeconds.trim()
          ? Math.max(1000, Math.round(parseFloat(timeoutSeconds) * 1000))
          : undefined;

      if (isEditing) {
        const updates: Partial<StoredServer> = {
          name,
          serverKind,
          connectionConfig,
          baseUrl: serverKind === "http" ? baseUrl : "",
          authType: serverKind === "http" ? authType : "none",
          authConfig: serverKind === "http" ? authConfig : "{}",
          isProduction,
          timeoutMs: validTimeoutMs,
          graphqlPath: serverKind === "http" ? paths.graphqlPath : undefined,
          restPath: serverKind === "http" ? paths.restPath : undefined,
        };
        await updateServer(editingServer.id, updates);
      } else {
        await addServer(
          name,
          serverKind === "http" ? baseUrl : "",
          serverKind === "http" ? authType : "none",
          serverKind === "http" ? authConfig : "{}",
          isProduction,
          serverKind === "http" ? {
            ...(paths.graphqlPath !== undefined ? { graphqlPath: paths.graphqlPath } : {}),
            ...(paths.restPath !== undefined ? { restPath: paths.restPath } : {}),
          } : undefined,
          { serverKind, connectionConfig },
          validTimeoutMs,
        );
        onClose();
        return;
      }
      toast("Server updated", "success");
    } catch (error) {
      toast(error instanceof Error ? error.message : "Failed to save server", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      void handleSave();
    }
    if (event.key === "Escape") {
      onClose();
    }
  };

  const handleCopyHeaderScriptPrompt = async () => {
    const prompt = buildHeaderScriptAgentPrompt({
      serverName: name,
      baseUrl,
      graphqlPath,
      restPath,
    });
    await navigator.clipboard.writeText(prompt);
    setScriptPromptCopied(true);
    window.setTimeout(() => setScriptPromptCopied(false), 1500);
  };

  const runSharingAction = async (action: () => Promise<void>) => {
    setSharingBusy(true);
    try {
      await action();
      await Promise.all([loadMembers(), loadPendingInvites()]);
    } catch (error) {
      console.error("[sharing] action failed:", error);
      const message =
        (error instanceof Error && error.message) ||
        (typeof error === "string" && error) ||
        (error && typeof error === "object" && "toString" in error ? String(error) : "") ||
        "Sharing action failed (see console for details)";
      toast(message, "error");
    } finally {
      setSharingBusy(false);
    }
  };

  const handleCreateOrUpdateShare = async () => {
    if (!currentServerId) return;
    await runSharingAction(async () => {
      await createOrUpdateSharedConfig(currentServerId);
      toast(isShared ? "Shared server updated" : "Shared server created", "success");
    });
  };

  const handleInvite = async () => {
    if (!sharedConfigId || !inviteEmail.trim()) return;
    const email = inviteEmail.trim();
    await runSharingAction(async () => {
      await inviteToSharedConfig(sharedConfigId!, email);
      toast(`Invite sent to ${email}`, "success");
      setInviteEmail("");
    });
  };

  const handleCopyInviteLink = async (inviteId: string, inviteUrl: string) => {
    await navigator.clipboard.writeText(inviteUrl);
    setCopiedInviteId(inviteId);
    toast("Invite link copied", "success");
    window.setTimeout(() => setCopiedInviteId((current) => (current === inviteId ? null : current)), 1500);
  };

  const handleResendInvite = async (invite: SharedConfigPendingInvite) => {
    if (!sharedConfigId) return;
    await runSharingAction(async () => {
      await resendSharedConfigInvite(sharedConfigId, invite.id);
      toast(`Invite resent to ${invite.email}`, "success");
    });
  };

  const handleRevokeInvite = async (invite: SharedConfigPendingInvite) => {
    if (!sharedConfigId) return;
    if (!confirm(`Revoke the pending invite for ${invite.email}?`)) return;
    await runSharingAction(async () => {
      await revokeSharedConfigInvite(sharedConfigId, invite.id);
      toast(`Invite revoked for ${invite.email}`, "success");
    });
  };

  const handleKick = async (member: SharedConfigMember) => {
    if (!sharedConfigId) return;
    if (!confirm(`Remove ${member.displayName || member.email} from this shared server?`)) return;
    await runSharingAction(async () => {
      await kickSharedConfigMember(sharedConfigId!, member.userId);
      toast(`${member.displayName || member.email} removed`, "success");
    });
  };

  const handleChangeRole = async (member: SharedConfigMember, role: "owner" | "editor") => {
    if (!sharedConfigId || member.role === role) return;
    await runSharingAction(async () => {
      await changeSharedConfigMemberRole(sharedConfigId!, member.userId, role);
      toast(`${member.displayName || member.email} is now ${role}`, "success");
    });
  };

  const handleTransferOwnership = async () => {
    if (!sharedConfigId || !transferTarget) return;
    if (!confirm(`Transfer ownership to ${transferTarget.displayName || transferTarget.email}? You will become an editor.`)) {
      setTransferTarget(null);
      return;
    }
    await runSharingAction(async () => {
      await transferSharedConfigOwnership(sharedConfigId!, transferTarget!.userId);
      toast(`Ownership transferred to ${transferTarget!.displayName || transferTarget!.email}`, "success");
      setTransferTarget(null);
    });
  };

  const handleDuplicateServer = async () => {
    if (!editingServer) return;
    const nextName = window.prompt("Name for the copy:", `${name.trim() || editingServer.name} (copy)`);
    if (!nextName?.trim()) return;

    setSharingBusy(true);
    try {
      await addServer(
        nextName.trim(),
        serverKind === "http" ? baseUrl : "",
        serverKind === "http" ? authType : "none",
        serverKind === "http" ? serializeAuthConfig(authType, {
          token,
          headerName,
          apiKey,
          customMode,
          customHeaders,
          customScript,
        }) : "{}",
        isProduction,
        serverKind === "http" ? {
          ...(normalizeOptionalPath(graphqlPath) !== undefined ? { graphqlPath: normalizeOptionalPath(graphqlPath)! } : {}),
          ...(normalizeOptionalPath(restPath) !== undefined ? { restPath: normalizeOptionalPath(restPath)! } : {}),
        } : undefined,
        {
          serverKind,
          connectionConfig: serverKind === "http"
            ? {
                kind: "http",
                baseUrl,
                ...(normalizeOptionalPath(graphqlPath) !== undefined ? { graphqlPath: normalizeOptionalPath(graphqlPath)! } : {}),
                ...(normalizeOptionalPath(restPath) !== undefined ? { restPath: normalizeOptionalPath(restPath)! } : {}),
              }
            : serverKind === "mongo"
              ? { kind: "mongo", connectionUri, database }
              : { kind: "postgres", connectionUri, ...(defaultSchema.trim() ? { defaultSchema: defaultSchema.trim() } : {}) },
        },
      );
      toast("Server duplicated", "success");
    } catch (error) {
      toast(error instanceof Error ? error.message : "Failed to duplicate server", "error");
    } finally {
      setSharingBusy(false);
    }
  };

  const handleDeleteServer = async () => {
    if (!editingServer) return;
    const deleteMessage = isShared && isOwner
      ? "Delete this server, all of its queries, and the shared server for everyone?"
      : "Delete this server and all of its queries?";
    if (!confirm(deleteMessage)) return;
    setSharingBusy(true);
    try {
      if (isShared && isOwner) {
        await deleteCurrentSharedConfig(editingServer.id);
      }
      await deleteServer(editingServer.id);
      onClose();
    } catch (error) {
      toast(error instanceof Error ? error.message : "Failed to delete server", "error");
    } finally {
      setSharingBusy(false);
    }
  };

  const handleIntrospect = async () => {
    if (!editingServer) return;
    setSharingBusy(true);
    try {
      await introspectSchema(editingServer.id);
      toast(serverKind === "http" ? "Schema introspection finished" : "Catalog refresh finished", "success");
    } catch (error) {
      toast(error instanceof Error ? error.message : (serverKind === "http" ? "Schema introspection failed" : "Catalog refresh failed"), "error");
    } finally {
      setSharingBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={(event) => event.target === event.currentTarget && onClose()}
      onKeyDown={handleKeyDown}
    >
      <div className="flex max-h-[92vh] w-full max-w-6xl flex-col overflow-hidden rounded-xl border border-border bg-surface-raised shadow-2xl">
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <div>
            <h2 className="text-sm font-semibold text-zinc-200">
              {isEditing ? t("Edit Server") : t("Add Server")}
            </h2>
            <p className="mt-1 text-xs text-zinc-500">
              {isEditing
                ? t("Server settings, saved queries and sharing status live here. AdHoc requests stay local.")
                : t("Create a local server or connect a shared one. AdHoc requests always stay local.")}
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded p-1 text-zinc-500 hover:bg-surface-raised hover:text-zinc-300"
            title={t("Close")}
          >
            <X size={14} />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5">
          <div className="space-y-5">
            <div className="space-y-4">
              <div>
                <label className="mb-1 block text-[11px] font-medium text-zinc-500">{t("Target Kind")}</label>
                <select
                  value={serverKind}
                  onChange={(event) => setServerKind(event.target.value as ServerKind)}
                  className="w-full rounded bg-surface px-3 py-1.5 text-sm text-zinc-200 outline-none ring-1 ring-border focus:ring-accent"
                >
                  <option value="http">{t("HTTP / GraphQL / REST")}</option>
                  <option value="mongo">{t("MongoDB")}</option>
                  <option value="postgres">{t("PostgreSQL")}</option>
                </select>
              </div>

              <div>
                <label className="mb-1 block text-[11px] font-medium text-zinc-500">{t("Name")}</label>
                <input
                  type="text"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  placeholder={t("My API")}
                  autoFocus
                  className="w-full rounded bg-surface px-3 py-1.5 text-sm text-zinc-200 outline-none ring-1 ring-border placeholder:text-zinc-700 focus:ring-accent"
                />
              </div>

              {isHttp ? (
                <>
                  <div>
                    <label className="mb-1 block text-[11px] font-medium text-zinc-500">{t("Base URL")}</label>
                    <VaultInsertField
                      type="text"
                      value={baseUrl}
                      onChange={setBaseUrl}
                      placeholder={t("https://api.example.com")}
                      serverId={currentServerId}
                      preferredKind="config"
                      inputClassName="w-full rounded bg-surface px-3 py-1.5 text-sm font-mono text-zinc-200 outline-none ring-1 ring-border placeholder:text-zinc-700 focus:ring-accent"
                    />
                  </div>

                  <div className="grid gap-3 sm:grid-cols-2">
                    <div>
                      <label className="mb-1 block text-[11px] font-medium text-zinc-500">{t("GraphQL Path")}</label>
                      <VaultInsertField
                        type="text"
                        value={graphqlPath}
                        onChange={setGraphqlPath}
                        placeholder={t("/graphql")}
                        serverId={currentServerId}
                        preferredKind="config"
                        inputClassName="w-full rounded bg-surface px-3 py-1.5 text-sm font-mono text-zinc-200 outline-none ring-1 ring-border placeholder:text-zinc-700 focus:ring-accent"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-[11px] font-medium text-zinc-500">{t("REST Path")}</label>
                      <VaultInsertField
                        type="text"
                        value={restPath}
                        onChange={setRestPath}
                        placeholder={t("/api")}
                        serverId={currentServerId}
                        preferredKind="config"
                        inputClassName="w-full rounded bg-surface px-3 py-1.5 text-sm font-mono text-zinc-200 outline-none ring-1 ring-border placeholder:text-zinc-700 focus:ring-accent"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="mb-1 block text-[11px] font-medium text-zinc-500">{t("Authentication")}</label>
                    <select
                      value={authType}
                      onChange={(event) => setAuthType(event.target.value as AuthType)}
                      className="w-full rounded bg-surface px-3 py-1.5 text-sm text-zinc-200 outline-none ring-1 ring-border focus:ring-accent"
                    >
                      <option value="none">{t("None")}</option>
                      <option value="bearer">{t("Bearer Token")}</option>
                      <option value="apikey">{t("API Key")}</option>
                      <option value="custom">{t("Custom Headers")}</option>
                    </select>
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <label className="mb-1 block text-[11px] font-medium text-zinc-500">{t("Connection URI")}</label>
                    <VaultInsertField
                      type="text"
                      value={connectionUri}
                      onChange={setConnectionUri}
                      placeholder={serverKind === "mongo" ? "mongodb://localhost:27017" : "postgres://user:pass@localhost:5432/db"}
                      serverId={currentServerId}
                      preferredKind="config"
                      inputClassName="w-full rounded bg-surface px-3 py-1.5 text-sm font-mono text-zinc-200 outline-none ring-1 ring-border placeholder:text-zinc-700 focus:ring-accent"
                    />
                  </div>
                  {serverKind === "mongo" ? (
                    <div>
                      <label className="mb-1 block text-[11px] font-medium text-zinc-500">{t("Database")}</label>
                      <VaultInsertField
                        type="text"
                        value={database}
                        onChange={setDatabase}
                        placeholder={t("app")}
                        serverId={currentServerId}
                        preferredKind="config"
                        inputClassName="w-full rounded bg-surface px-3 py-1.5 text-sm font-mono text-zinc-200 outline-none ring-1 ring-border placeholder:text-zinc-700 focus:ring-accent"
                      />
                    </div>
                  ) : (
                    <div>
                      <label className="mb-1 block text-[11px] font-medium text-zinc-500">{t("Default Schema")}</label>
                      <input
                        type="text"
                        value={defaultSchema}
                        onChange={(event) => setDefaultSchema(event.target.value)}
                        placeholder={t("public")}
                        className="w-full rounded bg-surface px-3 py-1.5 text-sm font-mono text-zinc-200 outline-none ring-1 ring-border placeholder:text-zinc-700 focus:ring-accent"
                      />
                    </div>
                  )}
                </>
              )}

              {isHttp && authType === "bearer" && (
                <>
                  <div>
                    <label className="mb-1 block text-[11px] font-medium text-zinc-500">{t("Token")}</label>
                    <VaultInsertField
                      type="password"
                      value={token}
                      onChange={setToken}
                      placeholder={`${t("Bearer token or")} {{secret:key}} ${t("reference")}`}
                      serverId={currentServerId}
                      preferredKind="secret"
                      inputClassName="w-full rounded bg-surface px-3 py-1.5 text-sm font-mono text-zinc-200 outline-none ring-1 ring-border placeholder:text-zinc-700 focus:ring-accent"
                    />
                  </div>
                  <p className="text-[10px] text-zinc-600">
                    {t("Token scripts now belong under Custom Headers. Static tokens can use vault references directly.")}
                  </p>
                </>
              )}

              {isHttp && authType === "apikey" && (
                <>
                  <div>
                    <label className="mb-1 block text-[11px] font-medium text-zinc-500">{t("Header Name")}</label>
                    <input
                      type="text"
                      value={headerName}
                      onChange={(event) => setHeaderName(event.target.value)}
                      placeholder={t("X-API-Key")}
                      className="w-full rounded bg-surface px-3 py-1.5 text-sm font-mono text-zinc-200 outline-none ring-1 ring-border placeholder:text-zinc-700 focus:ring-accent"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-[11px] font-medium text-zinc-500">{t("API Key")}</label>
                    <VaultInsertField
                      type="password"
                      value={apiKey}
                      onChange={setApiKey}
                      placeholder={`${t("API key or")} {{secret:key}} ${t("reference")}`}
                      serverId={currentServerId}
                      preferredKind="secret"
                      inputClassName="w-full rounded bg-surface px-3 py-1.5 text-sm font-mono text-zinc-200 outline-none ring-1 ring-border placeholder:text-zinc-700 focus:ring-accent"
                    />
                  </div>
                </>
              )}

              {isHttp && authType === "custom" && (
                <div className="space-y-3">
                  <div className="flex flex-wrap gap-1">
                    <button
                      type="button"
                      onClick={() => setCustomMode("static")}
                      className={`rounded px-2.5 py-1 text-[11px] font-medium ${
                        customMode === "static"
                          ? "bg-accent text-on-accent"
                          : "bg-surface text-zinc-500 ring-1 ring-border hover:text-zinc-300"
                      }`}
                    >
                      Static Headers
                    </button>
                    <button
                      type="button"
                      onClick={() => setCustomMode("script")}
                      className={`rounded px-2.5 py-1 text-[11px] font-medium ${
                        customMode === "script"
                          ? "bg-accent text-on-accent"
                          : "bg-surface text-zinc-500 ring-1 ring-border hover:text-zinc-300"
                      }`}
                    >
                      Header Script
                    </button>
                  </div>

                  {customMode === "static" ? (
                    <CustomHeadersEditor
                      value={customHeaders}
                      onChange={setCustomHeaders}
                      serverId={currentServerId}
                    />
                  ) : (
                    <div>
                      <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
                        <label className="block text-[11px] font-medium text-zinc-500">
                          Script (async function body)
                        </label>
                        <button
                          type="button"
                          onClick={handleCopyHeaderScriptPrompt}
                          className="inline-flex items-center gap-1 rounded border border-border bg-surface px-2 py-1 text-[10px] font-medium text-zinc-400 hover:text-zinc-200"
                        >
                          {scriptPromptCopied ? <Check size={11} className="text-success" /> : <Copy size={11} />}
                          {scriptPromptCopied ? "Prompt Copied" : "Copy LLM Prompt"}
                        </button>
                      </div>
                      <textarea
                        value={customScript}
                        onChange={(event) => setCustomScript(event.target.value)}
                        placeholder={'const token = await secret("sympathy.client_token");\nreturn {\n  headers: {\n    Authorization: `Bearer ${token}`,\n    "X-Tenant": await config("sympathy.tenant"),\n  },\n  expiresAt: new Date(Date.now() + 55 * 60 * 1000).toISOString(),\n};'}
                        rows={9}
                        spellCheck={false}
                        className="w-full resize-none rounded bg-surface px-3 py-1.5 font-mono text-xs text-zinc-200 outline-none ring-1 ring-border placeholder:text-zinc-700 focus:ring-accent"
                      />
                      <p className="mt-1 text-[10px] text-zinc-600">
                        {t("Expected shape:")} <span className="font-mono text-zinc-500">{"{ headers, expiresAt }"}</span>. {t("Plain header objects still work, but")} <span className="font-mono text-zinc-500">expiresAt</span> {t("is the intended cache contract.")}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {isEditing && (
              <div className="space-y-4">
                <div className="rounded-xl border border-border bg-surface px-4 py-4">
                  <div className="mb-3 text-xs font-semibold uppercase tracking-wide text-zinc-400">{t("Server Tools")}</div>
                  <div className="grid gap-2">
                    <button
                      type="button"
                      onClick={handleIntrospect}
                      disabled={sharingBusy}
                      className="flex items-center justify-between rounded border border-border bg-surface-raised px-3 py-2 text-xs text-zinc-300 hover:bg-surface disabled:opacity-50"
                    >
                      <span>{serverKind === "http" ? t("Refresh Schema") : t("Refresh Catalog")}</span>
                      <RefreshCw size={12} />
                    </button>
                    <button
                      type="button"
                      onClick={handleDuplicateServer}
                      disabled={sharingBusy}
                      className="flex items-center justify-between rounded border border-border bg-surface-raised px-3 py-2 text-xs text-zinc-300 hover:bg-surface disabled:opacity-50"
                    >
                      <span>{t("Duplicate Server")}</span>
                      <Copy size={12} />
                    </button>
                    <button
                      type="button"
                      onClick={handleDeleteServer}
                      disabled={sharingBusy}
                      className="flex items-center justify-between rounded border border-danger/30 bg-danger-bg px-3 py-2 text-xs text-danger hover:bg-danger-bg/80 disabled:opacity-50"
                    >
                      <span>{t("Delete Server")}</span>
                      <Trash2 size={12} />
                    </button>
                  </div>
                </div>

                <div className="rounded-xl border border-border bg-surface px-4 py-4">
                  <div className="mb-3 flex items-center justify-between gap-3">
                    <div>
                      <div className="text-xs font-semibold uppercase tracking-wide text-zinc-400">{t("Mit Team teilen")}</div>
                      <div className="mt-1 text-xs text-zinc-500">Server und gespeicherte Queries werden mit den eingeladenen Teammitgliedern geteilt. AdHoc GQL und AdHoc REST bleiben lokal. Deine eigenen Geräte synchronisieren ohnehin automatisch — Sharing ist nur für Kollaboration mit anderen.</div>
                    </div>
                    {isShared && currentUserRole && (
                      <span className="rounded bg-warning-bg px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-warning">
                        {currentUserRole}
                      </span>
                    )}
                  </div>

                  <div className="mb-3 flex items-start gap-2 rounded-lg border border-warning/30 bg-warning-bg px-3 py-2 text-[11px] text-warning">
                    <ShieldAlert size={14} className="mt-0.5 shrink-0" />
                    <div>Everything in this server config and its saved queries is shared. Use vault references for anything sensitive.</div>
                  </div>

                  {isShared && sharedConfigId ? (
                    <div className="space-y-3">
                      {/* Members list */}
                      <div>
                        <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-zinc-400">{t("Members")}</div>
                        <div className="space-y-2">
                          {members.length > 0 ? members.map((member) => {
                            const isSelf = member.userId === currentUser?.id;
                            return (
                              <div key={member.userId} className="rounded-lg border border-border bg-surface-raised px-3 py-2">
                                <div className="flex items-center gap-3">
                                  <div className="shrink-0">
                                    {member.avatarUrl ? (
                                      <img src={member.avatarUrl} alt="" className="h-7 w-7 rounded-full" />
                                    ) : (
                                      <div className="flex h-7 w-7 items-center justify-center rounded-full bg-surface">
                                        <User size={14} className="text-zinc-500" />
                                      </div>
                                    )}
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-1.5">
                                      <span className="truncate text-xs font-medium text-zinc-200">{member.displayName}</span>
                                      {isSelf && (
                                        <span className="rounded bg-surface px-1.5 py-0.5 text-[9px] uppercase tracking-wide text-zinc-400">{t("you")}</span>
                                      )}
                                      {member.role === "owner" && (
                                        <Crown size={10} className="shrink-0 text-warning" />
                                      )}
                                    </div>
                                    <div className="truncate text-[10px] text-zinc-500">{member.email}</div>
                                  </div>
                                  <div className="flex shrink-0 items-center gap-1.5">
                                    {isOwner && !isSelf ? (
                                      <>
                                        <select
                                          value={member.role}
                                          onChange={(e) => void handleChangeRole(member, e.target.value as "owner" | "editor")}
                                          disabled={sharingBusy}
                                          className="rounded bg-surface px-1.5 py-0.5 text-[10px] text-zinc-400 outline-none ring-1 ring-border focus:ring-accent disabled:opacity-50"
                                        >
                                          <option value="editor">editor</option>
                                          <option value="owner">owner</option>
                                        </select>
                                        <button
                                          type="button"
                                          onClick={() => void handleKick(member)}
                                          disabled={sharingBusy}
                                          className="rounded p-1 text-zinc-600 hover:text-danger disabled:opacity-50"
                                          title="Remove member"
                                        >
                                          <Trash2 size={12} />
                                        </button>
                                      </>
                                    ) : (
                                      <span className="rounded bg-surface px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-zinc-500">{member.role}</span>
                                    )}
                                  </div>
                                </div>
                                <div className="mt-1 pl-10 text-[10px] text-zinc-600">
                                  Joined {formatRelativeTime(member.joinedAt)} · Last seen {formatRelativeTime(member.lastSeenAt)}
                                </div>
                              </div>
                            );
                          }) : (
                            <div className="rounded-lg border border-dashed border-border px-3 py-3 text-xs text-zinc-500">
                              No members recorded yet.
                            </div>
                          )}
                        </div>
                      </div>

                      {isOwner && (
                        <div>
                          <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-zinc-400">{t("Pending Invites")}</div>
                          <div className="space-y-2">
                            {pendingInvites.length > 0 ? pendingInvites.map((invite) => (
                              <div key={invite.id} className="rounded-lg border border-border bg-surface-raised px-3 py-2">
                                <div className="flex items-center gap-3">
                                  <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-surface">
                                    <Mail size={14} className="text-zinc-500" />
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <div className="truncate text-xs font-medium text-zinc-200">{invite.email}</div>
                                    <div className="text-[10px] text-zinc-500">
                                      Invited {formatRelativeTime(invite.createdAt)} · Expires {formatTimeUntil(invite.expiresAt)}
                                    </div>
                                  </div>
                                  <span className="rounded bg-surface px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-zinc-500">
                                    {invite.role}
                                  </span>
                                </div>
                                <div className="mt-2 flex flex-wrap items-center justify-end gap-1.5 pl-10">
                                  <button
                                    type="button"
                                    onClick={() => void handleCopyInviteLink(invite.id, invite.inviteUrl)}
                                    className="inline-flex items-center gap-1 rounded border border-border px-2 py-1 text-[10px] font-medium text-zinc-300 transition-colors hover:bg-surface hover:text-white"
                                  >
                                    {copiedInviteId === invite.id ? <Check size={11} /> : <Copy size={11} />}
                                    {copiedInviteId === invite.id ? "Copied" : "Copy Link"}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => void handleResendInvite(invite)}
                                    disabled={sharingBusy}
                                    className="inline-flex items-center gap-1 rounded border border-border px-2 py-1 text-[10px] font-medium text-zinc-300 transition-colors hover:bg-surface hover:text-white disabled:opacity-40"
                                  >
                                    <RefreshCw size={11} />
                                    Resend
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => void handleRevokeInvite(invite)}
                                    disabled={sharingBusy}
                                    className="inline-flex items-center gap-1 rounded border border-danger/30 px-2 py-1 text-[10px] font-medium text-danger transition-colors hover:bg-danger-bg disabled:opacity-40"
                                  >
                                    <Trash2 size={11} />
                                    Revoke
                                  </button>
                                </div>
                              </div>
                            )) : (
                              <div className="rounded-lg border border-dashed border-border px-3 py-3 text-xs text-zinc-500">
                                No pending invites right now.
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Invite by email */}
                      <div>
                        <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-zinc-400">{t("Invite by Email")}</div>
                        <div className="flex gap-2">
                          <div className="relative flex-1">
                            <Mail size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-600" />
                            <input
                              type="email"
                              value={inviteEmail}
                              onChange={(e) => setInviteEmail(e.target.value)}
                              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); void handleInvite(); } }}
                              placeholder="colleague@example.com"
                              className="w-full rounded bg-surface py-1.5 pl-8 pr-3 text-xs text-zinc-200 outline-none ring-1 ring-border placeholder:text-zinc-700 focus:ring-accent"
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() => void handleInvite()}
                            disabled={!inviteEmail.trim() || sharingBusy}
                            className="inline-flex items-center gap-1.5 rounded bg-brand px-3 py-1.5 text-xs font-semibold text-zinc-900 hover:bg-brand-light disabled:opacity-40"
                          >
                            <UserPlus size={12} />
                            Invite
                          </button>
                        </div>
                        <div className="mt-2 text-[10px] text-zinc-500">
                          New invites show up above with a direct link you can copy, resend, or revoke.
                        </div>
                      </div>

                      {/* Transfer ownership */}
                      {isOwner && members.filter((m) => m.userId !== currentUser?.id).length > 0 && (
                        <div>
                          <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-zinc-400">{t("Transfer Ownership")}</div>
                          {transferTarget ? (
                            <div className="flex items-center gap-2">
                              <span className="flex-1 text-xs text-zinc-300">
                                Transfer to <span className="font-medium">{transferTarget.displayName || transferTarget.email}</span>?
                              </span>
                              <button
                                type="button"
                                onClick={() => void handleTransferOwnership()}
                                disabled={sharingBusy}
                                className="rounded bg-danger px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-danger/80 disabled:opacity-40"
                              >
                                Confirm
                              </button>
                              <button
                                type="button"
                                onClick={() => setTransferTarget(null)}
                                className="rounded px-2.5 py-1 text-[11px] font-medium text-zinc-400 hover:text-zinc-200"
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <select
                              value=""
                              onChange={(e) => {
                                const target = members.find((m) => m.userId === e.target.value);
                                if (target) setTransferTarget(target);
                              }}
                              className="w-full rounded bg-surface px-3 py-1.5 text-xs text-zinc-400 outline-none ring-1 ring-border focus:ring-accent"
                            >
                              <option value="" disabled>Select new owner...</option>
                              {members.filter((m) => m.userId !== currentUser?.id).map((m) => (
                                <option key={m.userId} value={m.userId}>{m.displayName || m.email}</option>
                              ))}
                            </select>
                          )}
                        </div>
                      )}

                      {/* Update shared server */}
                      <button
                        type="button"
                        onClick={handleCreateOrUpdateShare}
                        disabled={sharingBusy}
                        className="inline-flex w-full items-center justify-center gap-2 rounded border border-border bg-surface-raised px-3 py-2 text-xs font-medium text-zinc-300 hover:bg-surface disabled:opacity-40"
                      >
                        <RefreshCw size={12} />
                        Update Shared Server
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="rounded-lg border border-dashed border-border px-3 py-3 text-xs text-zinc-500">
                        Bereits über deine eigenen Geräte synchronisiert. Klicke unten, um diesen Server mit Teammitgliedern zu teilen.
                      </div>
                      <button
                        type="button"
                        onClick={handleCreateOrUpdateShare}
                        disabled={sharingBusy}
                        className="inline-flex w-full items-center justify-center gap-2 rounded bg-brand px-3 py-2 text-xs font-semibold text-zinc-900 hover:bg-brand-light disabled:opacity-40"
                      >
                        <WandSparkles size={12} />
                        Mit Team teilen
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border px-5 py-4">
          <div className="flex flex-wrap items-center gap-4">
            <label className="flex cursor-pointer items-center gap-2">
              <input
                type="checkbox"
                checked={isProduction}
                onChange={(event) => setIsProduction(event.target.checked)}
                className="h-3.5 w-3.5 rounded border-border accent-danger"
              />
              <span className="text-[11px] font-medium text-zinc-500">{t("Production Environment")}</span>
              {isProduction && (
                <span className="rounded bg-danger-bg px-1.5 py-0.5 text-[10px] font-bold text-danger">{t("PROD")}</span>
              )}
            </label>
            <label className="flex items-center gap-1.5">
              <span className="text-[11px] font-medium text-zinc-500">{t("Timeout")}</span>
              <input
                type="number"
                min="1"
                step="1"
                placeholder="300"
                value={timeoutSeconds}
                onChange={(event) => setTimeoutSeconds(event.target.value)}
                disabled={noTimeout}
                className="w-16 rounded border border-border bg-surface-sunken px-1.5 py-0.5 text-[11px] text-zinc-300 placeholder:text-zinc-600 disabled:opacity-40"
              />
              <span className="text-[11px] text-zinc-600">s</span>
            </label>
            <label className="flex cursor-pointer items-center gap-2">
              <input
                type="checkbox"
                checked={noTimeout}
                onChange={(event) => setNoTimeout(event.target.checked)}
                className="h-3.5 w-3.5 rounded border-border accent-primary"
              />
              <span className="text-[11px] font-medium text-zinc-500">{t("No timeout")}</span>
            </label>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-2">
            <button
              onClick={onClose}
              className="rounded px-3 py-1.5 text-xs font-medium text-zinc-400 hover:bg-surface-raised hover:text-zinc-200"
            >
              Cancel
            </button>
            <button
              onClick={() => void handleSave()}
              disabled={!canSave || saving}
              className="rounded bg-brand px-4 py-1.5 text-xs font-semibold text-zinc-900 hover:bg-brand-light disabled:cursor-not-allowed disabled:opacity-40"
            >
              {saving ? "Saving..." : isEditing ? "Update Server" : "Add Server"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
